import React from 'react';
import { MAX_PIECE_PRICE_CENTS } from '../../services/categoryPricing';
import RetailCategoryPrices from './RetailCategoryPrices';
import { usd } from '../distributor-cash/cashMoney';
import { useLocale } from '../../i18n/LanguageContext';
import { cashReportMoney } from '../distributor-cash/cashMoney';

// This is a business preview. The export keeps the server's complete columns.
const PREVIEW_COLUMNS = {
  customers: [
    'customer',
    'status',
    'categoryRates',
    'ratePerUnit',
    'creditLimit',
    'carrier',
    'createdAt',
  ],
  orders: ['orderId', 'customer', 'product', 'quantity', 'amount', 'status'],
  ivr: ['requestId', 'customer', 'status', 'createdAt', 'updatedAt'],
  shipments: ['orderId', 'customer', 'status', 'carrier', 'trackingNumber', 'estimatedDelivery'],
  purchases: ['createdAt', 'familyKey', 'units', 'amount', 'status', 'method'],
  bank: ['createdAt', 'familyKey', 'status', 'unitsDelta'],
  cash_bank: ['createdAt', 'orderNumber', 'status', 'amount', 'balanceAfter'],
  support: ['reference', 'customer', 'status', 'createdAt', 'lastMessageAt'],
  resources: ['title', 'category', 'fileName', 'publishedAt'],
};
const LABELS = {
  orderId: 'Order',
  requestId: 'Request',
  reference: 'Request',
  customer: 'Customer',
  product: 'Product',
  quantity: 'Qty',
  units: 'Units',
  amount: 'Amount (USD)',
  categoryRates: 'Retail category prices (USD per piece)',
  ratePerUnit: 'Legacy resale rate (USD/unit)',
  creditLimit: 'Credit limit (USD)',
  carrier: 'Carrier',
  trackingNumber: 'Tracking number',
  estimatedDelivery: 'Expected delivery',
  createdAt: 'Date',
  updatedAt: 'Updated',
  familyKey: 'Product family',
  unitsDelta: 'Unit change',
  balanceAfter: 'Balance after (USD)',
  orderNumber: 'Reference',
  method: 'Payment method',
  title: 'Title',
  category: 'Category',
  fileName: 'File name',
  publishedAt: 'Published',
  lastMessageAt: 'Last activity',
  status: 'Status',
};
const NUMERIC = new Set([
  'quantity',
  'units',
  'unitsDelta',
  'amount',
  'balanceAfter',
  'ratePerUnit',
  'creditLimit',
]);
const REFERENCES = new Set(['orderId', 'orderNumber', 'requestId', 'reference', 'trackingNumber']);
const DATE = (key) => key.endsWith('At') || key === 'estimatedDelivery';
const present = (value) => value !== null && value !== undefined && value !== '';
const normalizeSize = (value) =>
  String(value).toLowerCase().replace(/cm|\s/g, '').replaceAll('×', 'x');
const sizeInName = (row) =>
  [...String(row.product).matchAll(/\d+(?:\.\d+)?\s*[x×]\s*\d+(?:\.\d+)?/gi)].some(
    ([match]) => normalizeSize(match) === normalizeSize(row.size)
  );
const display = (value) =>
  !present(value) ? '—' : typeof value === 'object' ? JSON.stringify(value) : String(value);
const tone = (status) =>
  ['enabled', 'completed', 'covered', 'credited', 'resolved'].includes(status)
    ? 'positive'
    : ['needs_review', 'pending', 'not_covered', 'waiting_on_user'].includes(status)
      ? 'warning'
      : status === 'rejected'
        ? 'negative'
        : 'neutral';

function DateValue({ value, locale }) {
  if (!present(value) || !Number.isFinite(Date.parse(value))) return display(value);
  const date = new Date(value);
  const full = date.toISOString().replace('T', ' ').replace('Z', ' UTC');
  return (
    <time dateTime={value} title={full} aria-label={full}>
      {date.toLocaleDateString(locale, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
      })}
    </time>
  );
}

function Cell({ dataset, row, column, t, statusLabels, locale }) {
  const value = row[column];
  const piecePrice =
    typeof row.pricePerPieceCents === 'string' && /^(0|[1-9]\d*)$/.test(row.pricePerPieceCents)
      ? Number(row.pricePerPieceCents)
      : row.pricePerPieceCents;
  const secondary = (content) => <span className="me-report-secondary-value">{content}</span>;
  if (DATE(column)) return <DateValue value={value} locale={locale} />;
  if (column === 'status')
    return (
      <>
        <span className="me-report-status" data-tone={tone(value)}>
          {t(statusLabels[value] || display(value))}
        </span>
        {dataset === 'purchases' &&
          row.returnReviewOpen === true &&
          secondary(t('Return review open'))}
        {dataset === 'purchases' &&
          present(row.paymentState) &&
          secondary(t(statusLabels[row.paymentState] || row.paymentState))}
      </>
    );
  if (column === 'orderNumber')
    return present(row.orderNumber)
      ? row.orderNumber
      : row.status === 'deposit'
        ? t('Deposit')
        : '—';
  if (column === 'categoryRates') return <RetailCategoryPrices settings={row} t={t} />;
  if (column === 'amount')
    return (
      <>
        <span className="me-report-primary-value">
          {!present(value)
            ? '—'
            : dataset === 'cash_bank'
              ? cashReportMoney(value)
              : display(value)}
        </span>
        {!present(value) && secondary(t(row.amountStatus || 'Amount unavailable'))}
        {row.pricingBasis === 'piece' &&
          Number.isSafeInteger(piecePrice) &&
          piecePrice >= 0 &&
          piecePrice <= MAX_PIECE_PRICE_CENTS &&
          secondary(t('Retail: {price} per piece', { price: usd(piecePrice) }))}
        {dataset !== 'cash_bank' &&
          row.pricingBasis !== 'piece' &&
          present(row.ratePerUnit) &&
          secondary(t('Rate: {rate} / unit', { rate: row.ratePerUnit }))}
      </>
    );
  if (column === 'balanceAfter')
    return <span className="me-report-primary-value">{cashReportMoney(value)}</span>;
  if (column === 'quantity')
    return (
      <>
        {display(value)}
        {row.pricingBasis === 'piece'
          ? secondary(t('{count} pieces', { count: value }))
          : present(row.units) && secondary(t('{count} units', { count: row.units }))}
      </>
    );
  if (column === 'product')
    return (
      <>
        <span className="me-report-primary-value">{display(value)}</span>
        {present(row.size) && !sizeInName(row) && secondary(row.size)}
      </>
    );
  if (column === 'orderId')
    return (
      <>
        <span className="me-report-primary-value">{display(value)}</span>
        {present(row.createdAt) && secondary(<DateValue value={row.createdAt} locale={locale} />)}
      </>
    );
  if (column === 'carrier')
    return { fedex: 'FedEx', ups: 'UPS', usps: 'USPS' }[value] || display(value);
  if (column === 'familyKey')
    return display(value)
      .replaceAll('_', ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  if (column === 'customer')
    return (
      <>
        <span className="me-report-primary-value">{display(value)}</span>
        {dataset === 'customers' &&
          secondary([row.deliveryCity, row.deliveryState].filter(present).join(', '))}
      </>
    );
  if (column === 'title') return <span className="me-report-primary-value">{display(value)}</span>;
  return typeof value === 'boolean' ? t(value ? 'Yes' : 'No') : display(value);
}

export default function DistributorReportTable({ dataset, data, page, t, statusLabels }) {
  const locale = useLocale();
  const available = new Set(data.columns.map(({ key }) => key));
  const columns = PREVIEW_COLUMNS[dataset].filter((key) => available.has(key));
  const label = (key) => {
    if (key === 'status')
      return t(
        dataset === 'customers'
          ? 'Ordering access'
          : ['bank', 'cash_bank'].includes(dataset)
            ? 'Type'
            : 'Status'
      );
    if (key === 'createdAt' && dataset === 'customers') return t('Added');
    if (key === 'amount' && dataset === 'cash_bank') return t('Movement (USD)');
    return t(LABELS[key]);
  };
  const className = (key) =>
    NUMERIC.has(key)
      ? 'me-report-number'
      : DATE(key)
        ? 'me-report-date-value'
        : REFERENCES.has(key)
          ? 'me-report-reference'
          : key === 'customer'
            ? 'me-report-customer'
            : ['product', 'title', 'fileName'].includes(key)
              ? 'me-report-product'
              : undefined;
  return (
    <div
      className="me-report-table-scroll"
      tabIndex={0}
      role="region"
      aria-label={t('Report records')}
    >
      <table className="me-distributor-report-table" role="table" aria-label={t('Report records')}>
        <thead>
          <tr role="row">
            {columns.map((key) => (
              <th key={key} scope="col" role="columnheader" className={className(key)}>
                {label(key)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row, index) => (
            <tr key={`${page}:${index}`} role="row">
              {columns.map((key) => (
                <td key={key} role="cell" className={className(key)}>
                  <span className="me-report-cell-label" aria-hidden="true">
                    {label(key)}
                  </span>
                  <div className="me-report-cell-value">
                    <Cell
                      dataset={dataset}
                      row={row}
                      column={key}
                      t={t}
                      statusLabels={statusLabels}
                      locale={locale}
                    />
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
